import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, Heart, User, Leaf, ChevronDown, ShoppingBag } from "lucide-react";
import { useBooking } from "../contexts/BookingContext";
import { useCart } from "../contexts/CartContext";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useSavedLooks } from "../contexts/SavedLooksContext";
import { useSiteTexts, t } from "../contexts/SiteTextsContext";

const API = process.env.REACT_APP_BACKEND_URL;
const TAGS_CACHE_KEY = "mahra_navbar_tags";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [hoveredCat, setHoveredCat] = useState(null);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [trialEnabled, setTrialEnabled] = useState(false);
  const searchInputRef = useRef(null);
  const hoverTimeout = useRef(null);
  const tagsFetched = useRef(false);
  const navigate = useNavigate();
  const { openDateModal, hasDateSelected } = useBooking();
  const { customer } = useCustomerAuth();
  const { cartCount } = useCart();
  const { count: savedCount } = useSavedLooks();
  const texts = useSiteTexts();

  useEffect(() => {
    fetch(`${API}/api/settings/categories`)
      .then(r => r.json())
      .then(d => setCategories(d.categories || []))
      .catch(() => {});

    // Trial visibility: only show nav link when admin has the type enabled.
    // Patch 2 — uses shared settings cache so we don't re-fetch on every mount.
    import("../lib/settingsCache").then(({ fetchBookingSettings }) =>
      fetchBookingSettings(`${API}/api`)
    )
      .then(d => {
        const trial = (d?.types || []).find(t => t.key === "trial");
        setTrialEnabled(Boolean(trial));
      })
      .catch(() => setTrialEnabled(false));

    // Check sessionStorage cache first
    try {
      const cached = sessionStorage.getItem(TAGS_CACHE_KEY);
      if (cached) {
        const { occasions: cachedOcc } = JSON.parse(cached);
        if (cachedOcc?.length) { setOccasions(cachedOcc); return; }
      }
    } catch (_) {}

    // No cache — defer the lightweight tags fetch
    if (tagsFetched.current) return;
    tagsFetched.current = true;

    const fetchTags = () => {
      fetch(`${API}/api/ai/tags`)
        .then(r => r.json())
        .then(d => {
          const allTags = [...(d.occasions || []), ...(d.look_types || [])];
          const unique = [...new Set(allTags.filter(Boolean))];
          setOccasions(unique);
          try { sessionStorage.setItem(TAGS_CACHE_KEY, JSON.stringify({ occasions: unique })); } catch (_) {}
        })
        .catch(() => {});
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(fetchTags);
    } else {
      setTimeout(fetchTags, 300);
    }
  }, []);

  const handleBookNow = () => { openDateModal(); };

  const openSearch = useCallback(() => { setIsSearchOpen(true); setSearchQuery(""); }, []);
  const closeSearch = useCallback(() => { setIsSearchOpen(false); setSearchQuery(""); }, []);
  const submitSearch = useCallback((q) => {
    const query = (q || searchQuery).trim();
    if (!query) return;
    setIsSearchOpen(false); setSearchQuery("");
    navigate(`/catalog?search=${encodeURIComponent(query)}`);
  }, [searchQuery, navigate]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeSearch(); };
    document.addEventListener("keydown", onKey);
    setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => document.removeEventListener("keydown", onKey);
  }, [isSearchOpen, closeSearch]);
  
  // Handle category click - navigate directly, no blocking
  const handleCategoryClick = (e, url) => {
    setIsOpen(false);
    setHoveredCat(null);
  };

  const handleMouseEnter = (catId) => {
    clearTimeout(hoverTimeout.current);
    setHoveredCat(catId);
  };
  const handleMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setHoveredCat(null), 150);
  };

  // Dynamic nav logic: if only 1 parent enabled, show its subcategories directly
  const enabledParents = categories.filter(c => c.enabled !== false);
  const singleParentMode = enabledParents.length === 1 && enabledParents[0].children?.filter(c => c.enabled !== false).length > 0;
  const singleParent = singleParentMode ? enabledParents[0] : null;
  const navCategories = singleParentMode
    ? enabledParents[0].children.filter(c => c.enabled !== false)
    : enabledParents;

  // Build category URL: /{parentSlug}/{childSlug} or /{parentSlug}
  const buildCatUrl = (cat) => {
    if (singleParentMode && singleParent) {
      return `/${singleParent.slug || singleParent.id}/${cat.slug || cat.id}`;
    }
    return `/${cat.slug || cat.id}`;
  };
  const buildSubUrl = (parentCat, sub) => `/${parentCat.slug || parentCat.id}/${sub.slug || sub.id}`;

  return (
    <>
    <nav className="sticky top-0 z-50" style={{ backgroundColor: "#C9A0A0" }}>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between px-4 lg:px-10 h-16 lg:h-[4.5rem]">
          {/* Mobile: Hamburger */}
          <div className="lg:hidden flex-1">
            <button className="p-2 -ml-2 text-white" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
              {isOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
            </button>
          </div>

          {/* Desktop Left Nav — Dynamic Categories */}
          <div className="hidden lg:flex items-center gap-5 flex-1">
            {navCategories.map((cat) => {
              const enabledChildren = cat.children?.filter(c => c.enabled !== false) || [];
              const hasDropdown = !singleParentMode && enabledChildren.length > 0;
              return (
                <div
                  key={cat.id}
                  className="relative"
                  onMouseEnter={() => hasDropdown && handleMouseEnter(cat.id)}
                  onMouseLeave={() => hasDropdown && handleMouseLeave()}
                >
                  <Link
                    to={buildCatUrl(cat)}
                    onClick={(e) => handleCategoryClick(e, buildCatUrl(cat))}
                    className="flex items-center gap-0.5 text-[11px] tracking-[0.12em] uppercase text-white/85 hover:text-white font-medium transition-opacity py-2"
                    data-testid={`nav-cat-${cat.id}`}
                  >
                    {cat.name}
                    {hasDropdown && <ChevronDown className="w-3 h-3 ml-0.5" />}
                  </Link>
                  {hasDropdown && hoveredCat === cat.id && (
                    <div
                      className="absolute top-full left-0 pt-1 z-50"
                      onMouseEnter={() => handleMouseEnter(cat.id)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="bg-white shadow-lg border border-[#282828]/5 min-w-[180px] py-1">
                        {enabledChildren.map((sub) => (
                          <Link
                            key={sub.id}
                            to={buildSubUrl(cat, sub)}
                            onClick={(e) => handleCategoryClick(e, buildSubUrl(cat, sub))}
                            className="block px-4 py-2.5 text-[11px] text-[#282828]/70 hover:text-[#282828] hover:bg-[#FAF8F0] transition-colors"
                            data-testid={`nav-sub-${sub.id}`}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {/* Shop by Occasion — with dropdown */}
            <div
              className="relative"
              onMouseEnter={() => occasions.length > 0 && handleMouseEnter('occasions')}
              onMouseLeave={() => occasions.length > 0 && handleMouseLeave()}
            >
              <Link
                to="/looks"
                onClick={() => setHoveredCat(null)}
                className="flex items-center gap-0.5 text-[11px] tracking-[0.12em] uppercase text-white/85 hover:text-white font-medium transition-opacity py-2"
                data-testid="nav-shop-occasion"
              >
                Shop by Occasion
                {occasions.length > 0 && <ChevronDown className="w-3 h-3 ml-0.5" />}
              </Link>
              {occasions.length > 0 && hoveredCat === 'occasions' && (
                <div
                  className="absolute top-full left-0 pt-1 z-50"
                  onMouseEnter={() => handleMouseEnter('occasions')}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="bg-white shadow-lg border border-[#282828]/5 min-w-[180px] py-1">
                    <Link
                      to="/looks"
                      className="block px-4 py-2.5 text-[11px] text-[#9A7B4F] font-medium hover:bg-[#FAF8F0] transition-colors border-b border-[#282828]/5"
                      onClick={() => setHoveredCat(null)}
                    >
                      View All Looks
                    </Link>
                    {occasions.map(occ => (
                      <Link
                        key={occ}
                        to={`/looks?filter=${encodeURIComponent(occ)}`}
                        className="block px-4 py-2.5 text-[11px] text-[#282828]/70 hover:text-[#282828] hover:bg-[#FAF8F0] transition-colors"
                        onClick={() => setHoveredCat(null)}
                      >
                        {occ}
                      </Link>
                    ))}
                    <div className="border-t border-[#282828]/5 mt-1 pt-1">
                      <Link
                        to="/build-your-look"
                        className="block px-4 py-2.5 text-[11px] text-[#9A7B4F] font-medium hover:bg-[#FAF8F0] transition-colors"
                        onClick={() => setHoveredCat(null)}
                        data-testid="nav-build-your-look"
                      >
                        ✨ Build Your Own Look
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center Logo */}
          <Link to="/" className="flex items-center gap-1 flex-shrink-0" data-testid="nav-logo">
            <Leaf className="w-5 h-5 lg:w-6 lg:h-6 text-[#C5D5CC]" strokeWidth={1.5} fill="#C5D5CC" />
            <span className="text-xl lg:text-[22px] tracking-[0.22em] font-light text-white">MAHRA</span>
          </Link>

          {/* Desktop Right Nav + Icons */}
          <div className="hidden lg:flex items-center gap-5 flex-1 justify-end">
            <Link to="/our-story" className="text-[11px] tracking-[0.14em] uppercase text-white/85 hover:text-white font-medium" data-testid="nav-our-story">Our Story</Link>
            <Link to="/borrowing-terms" className="text-[11px] tracking-[0.14em] uppercase text-white/85 hover:text-white font-medium" data-testid="nav-our-process">Our Process</Link>
            {trialEnabled && (
              <Link to="/booking/trial" className="text-[11px] tracking-[0.14em] uppercase text-white/85 hover:text-white font-medium" data-testid="nav-trial">Trial Booking</Link>
            )}
            <button className="p-1.5 text-white/70 hover:text-white" onClick={openSearch} data-testid="nav-search-btn"><Search className="w-[17px] h-[17px]" strokeWidth={1.5} /></button>
            <Link to="/saved-looks" className="p-1.5 text-white/70 hover:text-white relative" data-testid="nav-heart-btn">
              <Heart className="w-[17px] h-[17px]" strokeWidth={1.5} />
              {savedCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#9A7B4F] text-white text-[9px] font-bold flex items-center justify-center rounded-full">{savedCount}</span>}
            </Link>
            <Link to="/cart" className="p-1.5 text-white/70 hover:text-white relative" data-testid="nav-cart-btn">
              <ShoppingBag className="w-[17px] h-[17px]" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#9A7B4F] text-white text-[9px] font-bold flex items-center justify-center rounded-full" data-testid="cart-badge">{cartCount}</span>
              )}
            </Link>
            <Link to={customer ? "/account" : "/login"} className="p-1.5 text-white/70 hover:text-white" data-testid="nav-login-btn"><User className="w-[17px] h-[17px]" strokeWidth={1.5} /></Link>
          </div>

          {/* Mobile Right Icons */}
          <div className="flex lg:hidden items-center justify-end gap-1 flex-1">
            <button className="p-2 text-white/70" onClick={openSearch} data-testid="nav-search-btn-mobile"><Search className="w-4 h-4" strokeWidth={1.5} /></button>
            <Link to="/cart" className="p-2 text-white/70 relative" data-testid="nav-cart-btn-mobile">
              <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
              {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#9A7B4F] text-white text-[8px] font-bold flex items-center justify-center rounded-full">{cartCount}</span>}
            </Link>
            <Link to={customer ? "/account" : "/login"} className="p-2 text-white/70" data-testid="nav-login-btn-mobile"><User className="w-4 h-4" strokeWidth={1.5} /></Link>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        {isOpen && (
          <div className="lg:hidden animate-fadeIn" style={{ backgroundColor: "#C9A0A0", borderTop: "1px solid rgba(255,255,255,0.15)" }}>
            <div className="px-6 py-5 space-y-0">
              {navCategories.map((cat) => {
                const enabledChildren = cat.children?.filter(c => c.enabled !== false) || [];
                const hasDropdown = !singleParentMode && enabledChildren.length > 0;
                return (
                  <div key={cat.id}>
                    <div className="flex items-center justify-between py-2.5 border-b border-white/10">
                      <Link
                        to={buildCatUrl(cat)}
                        className="text-[12px] tracking-[0.1em] uppercase text-white/85 font-medium"
                        onClick={(e) => handleCategoryClick(e, buildCatUrl(cat))}
                      >
                        {cat.name}
                      </Link>
                      {hasDropdown && (
                        <button onClick={() => setExpandedMobile(expandedMobile === cat.id ? null : cat.id)} className="text-white/40 p-1">
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedMobile === cat.id ? "rotate-180" : ""}`} />
                        </button>
                      )}
                    </div>
                    {hasDropdown && expandedMobile === cat.id && (
                      <div className="pl-4 pb-2">
                        {enabledChildren.map((sub) => (
                          <Link
                            key={sub.id}
                            to={buildSubUrl(cat, sub)}
                            className="block py-2 text-[11px] text-white/60 hover:text-white"
                            onClick={(e) => handleCategoryClick(e, buildSubUrl(cat, sub))}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <Link to="/looks" className="block text-[12px] tracking-[0.1em] uppercase py-2.5 text-white/85 font-medium border-b border-white/10" onClick={() => setIsOpen(false)}>Shop by Occasion</Link>
              <Link to="/build-your-look" className="block text-[12px] tracking-[0.1em] uppercase py-2.5 text-[#FFF5E6] font-medium border-b border-white/10" onClick={() => setIsOpen(false)} data-testid="nav-build-your-look-mobile">✨ Build Your Own Look</Link>
              {occasions.length > 0 && (
                <div>
                  <div className="flex items-center justify-between py-2.5 border-b border-white/10">
                    <span className="text-[12px] tracking-[0.1em] uppercase text-white/60 font-medium">Browse Occasions</span>
                    <button onClick={() => setExpandedMobile(expandedMobile === 'occasions' ? null : 'occasions')} className="text-white/40 p-1">
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedMobile === 'occasions' ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {expandedMobile === 'occasions' && (
                    <div className="pl-4 pb-2">
                      {occasions.map(occ => (
                        <Link key={occ} to={`/looks?filter=${encodeURIComponent(occ)}`} className="block py-2 text-[11px] text-white/60 hover:text-white" onClick={() => setIsOpen(false)}>
                          {occ}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <Link to="/our-story" className="block text-[12px] tracking-[0.1em] uppercase py-2.5 text-white/85 font-medium border-b border-white/10" onClick={() => setIsOpen(false)}>Our Story</Link>
              <Link to="/borrowing-terms" className="block text-[12px] tracking-[0.1em] uppercase py-2.5 text-white/85 font-medium border-b border-white/10" onClick={() => setIsOpen(false)}>Our Process</Link>
              {trialEnabled && (
                <Link to="/booking/trial" className="block text-[12px] tracking-[0.1em] uppercase py-2.5 text-white/85 font-medium border-b border-white/10" onClick={() => setIsOpen(false)} data-testid="nav-trial-mobile">Trial Booking</Link>
              )}
              <div className="pt-4 flex items-center gap-4">
                <Link to="/saved-looks" className="p-2 text-white/70" onClick={() => setIsOpen(false)}><Heart className="w-5 h-5" strokeWidth={1.5} /></Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>

    {/* Search Modal Overlay */}
    {isSearchOpen && (
      <div
        className="fixed inset-0 z-[60] flex items-start justify-center"
        style={{ background: "rgba(0,0,0,0.25)", backdropFilter: "blur(4px)" }}
        onClick={(e) => { if (e.target === e.currentTarget) closeSearch(); }}
        data-testid="search-overlay"
      >
        <div
          className="w-full max-w-[720px] mt-[12vh] mx-4 p-12 sm:p-14 relative"
          style={{
            backgroundColor: "#FAF8F0",
            animation: "searchSlideDown 0.35s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          <button
            onClick={closeSearch}
            className="absolute top-5 right-6 text-[#676664]/50 hover:text-[#676664] text-2xl font-light transition-opacity"
            data-testid="search-close-btn"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>

          <h2
            className="text-center mb-10"
            style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif", fontSize: "30px", fontWeight: 300, color: "#3D2B1F", letterSpacing: "0.02em" }}
          >
            Search the Collection
          </h2>

          <form onSubmit={(e) => { e.preventDefault(); submitSearch(); }} className="relative mb-8">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Lehengas, sarees, accessories..."
              className="w-full py-4 pr-12 bg-transparent border-b-[1.5px] outline-none text-lg font-light tracking-wide"
              style={{
                fontFamily: "'Montserrat', sans-serif",
                color: "#3D2B1F",
                borderColor: searchQuery ? "#9A7B4F" : "#C4A77D",
              }}
              data-testid="search-input"
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1"
              style={{ color: "#9A7B4F", opacity: 0.7 }}
              data-testid="search-submit-btn"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </form>

          <p
            className="mb-3"
            style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "11px", color: "#676664", opacity: 0.5, letterSpacing: "0.12em", textTransform: "uppercase" }}
          >
            Popular Searches
          </p>
          <div className="flex flex-wrap gap-2.5">
            {["Lehengas", "Sarees", "Indo-Western", "Anarkalis & Shararas", "Clutches & Potlis"].map((term) => (
              <button
                key={term}
                onClick={() => submitSearch(term)}
                className="px-5 py-2 text-xs tracking-wider border transition-all duration-200 hover:text-white"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  color: "#9A7B4F",
                  borderColor: "rgba(154,123,79,0.2)",
                  background: "transparent",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#9A7B4F"; e.currentTarget.style.borderColor = "#9A7B4F"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(154,123,79,0.2)"; }}
                data-testid={`search-chip-${term.toLowerCase()}`}
              >
                {term}
              </button>
            ))}
          </div>

          <p className="text-center mt-9" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "11px", color: "#676664", opacity: 0.35, letterSpacing: "0.04em" }}>
            Press <kbd className="inline-block px-2 py-0.5 border border-[#676664]/25 rounded text-[10px] mx-0.5">ESC</kbd> to close
            <span className="mx-1.5">&middot;</span>
            <kbd className="inline-block px-2 py-0.5 border border-[#676664]/25 rounded text-[10px] mx-0.5">Enter</kbd> to search
          </p>
        </div>
      </div>
    )}

    <style>{`
      @keyframes searchSlideDown {
        from { opacity: 0; transform: translateY(-30px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}</style>
    {/* Announcement Bar */}
    <div className="w-full py-1.5 sm:py-2.5 text-center" style={{ backgroundColor: "#B8C9BD" }} data-testid="announcement-bar">
      <p className="text-[9px] sm:text-[11px] tracking-[0.18em] uppercase font-medium" style={{ color: "#2D3A30" }}>
        {t(texts, "announcement_bar", "Now in Melbourne • Free pick up • Free delivery on orders over $175")}
      </p>
    </div>
    </>
  );
}
